/**
 * useRequestNotifications Hook
 *
 * Handles real-time request notifications via WebSocket and integrates
 * with React Query cache for automatic UI updates.
 *
 * Features:
 * - Listens to request events (created, approved, rejected, updated)
 * - Automatically invalidates React Query cache for two-way sync
 * - Provides notification state for UI feedback
 * - Role-based filtering (admins see all, users see their own)
 * - Properly handles nested payload structure from API
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  WSMessageType,
  wsService,
  type RequestApprovedPayload,
  type RequestCancelledPayload,
  type RequestCreatedPayload,
  type RequestRejectedPayload,
  type RequestUpdatedPayload,
} from "../api/websocketService";
import { homeQueryKeys } from "./useHomeQueries";
import { mediumStatusQueryKey } from "./useMediumStatusQuery";
import { profileQueryKeys } from "./useProfileQuery";

/**
 * Notification item type
 */
export interface RequestNotification {
  id: string;
  type: "created" | "approved" | "rejected" | "updated" | "cancelled";
  requestId: string;
  mediumId: string;
  mediumName?: string;
  requestType: string;
  terreiroId: string;
  timestamp: Date;
  read: boolean;
  message: string;
}

/**
 * Hook options
 */
interface UseRequestNotificationsOptions {
  /** Current user ID to filter user-specific notifications */
  userId?: string;
  /** Current medium ID to filter medium-specific notifications */
  mediumId?: string;
  /** Current terreiro ID to ignore cross-house request events */
  terreiroId?: string;
  /** Whether user is an admin (receives all notifications) */
  isAdmin?: boolean;
  /** Maximum notifications to keep in memory */
  maxNotifications?: number;
  /** Callback when a new notification is received */
  onNotification?: (notification: RequestNotification) => void;
}

/**
 * Hook return type
 */
interface UseRequestNotificationsReturn {
  /** List of notifications */
  notifications: RequestNotification[];
  /** Unread count */
  unreadCount: number;
  /** Mark a notification as read */
  markAsRead: (notificationId: string) => void;
  /** Mark all notifications as read */
  markAllAsRead: () => void;
  /** Clear all notifications */
  clearAll: () => void;
  /** Latest notification (for toast display) */
  latestNotification: RequestNotification | null;
  /** Clear the latest notification */
  clearLatest: () => void;
}

/**
 * Generate notification message based on type
 */
function generateNotificationMessage(
  type: RequestNotification["type"],
  payload: {
    mediumName?: string;
    requestType?: string;
    reason?: string;
  },
): string {
  const name = payload.mediumName || "Um médium";
  const requestType = getRequestTypeLabel(payload.requestType);

  switch (type) {
    case "created":
      return `${name} enviou uma solicitação de ${requestType}`;
    case "approved":
      return `Sua solicitação de ${requestType} foi aprovada!`;
    case "rejected":
      return payload.reason
        ? `Sua solicitação de ${requestType} foi recusada: ${payload.reason}`
        : `Sua solicitação de ${requestType} foi recusada`;
    case "updated":
      return `A solicitação de ${requestType} foi atualizada`;
    case "cancelled":
      return `A solicitação de ${requestType} foi cancelada`;
    default:
      return "Nova notificação de solicitação";
  }
}

function isForeignTerreiroEvent(
  currentTerreiroId: string | undefined,
  incomingTerreiroId: string | undefined,
): boolean {
  return Boolean(
    currentTerreiroId &&
      incomingTerreiroId &&
      incomingTerreiroId !== currentTerreiroId,
  );
}

/**
 * Get human-readable label for request type
 */
function getRequestTypeLabel(requestType?: string): string {
  if (!requestType) return "solicitação";

  const labels: Record<string, string> = {
    ACTIVITY_PARTICIPATION: "participação em atividade",
    EVENT_PARTICIPATION: "participação em evento",
    RITUAL_BATH: "banho ritual",
    MEDIUM_JOIN: "entrada no terreiro",
    MONTHLY_FEE: "mensalidade",
  };

  return labels[requestType] || requestType.toLowerCase().replace(/_/g, " ");
}

/**
 * Extract medium name from payload
 */
function getMediumNameFromPayload(payload: RequestCreatedPayload): string {
  if (!payload.medium?.user?.profile) return "Um médium";
  const { firstName, lastName } = payload.medium.user.profile;
  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;
  return "Um médium";
}

/**
 * useRequestNotifications Hook
 *
 * @example
 * ```tsx
 * const { notifications, unreadCount, latestNotification, clearLatest } =
 *   useRequestNotifications({
 *     userId: currentUser?.id,
 *     mediumId: currentMedium?.id,
 *     isAdmin: userRoles?.includes('Administrador'),
 *     onNotification: (notification) => {
 *       toast.info(notification.message);
 *     },
 *   });
 *
 * // Display unread badge
 * <Badge count={unreadCount}>
 *   <BellIcon />
 * </Badge>
 * ```
 */
export function useRequestNotifications(
  options: UseRequestNotificationsOptions = {},
): UseRequestNotificationsReturn {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    userId: _userId,
    mediumId,
    terreiroId: currentTerreiroId,
    isAdmin = false,
    maxNotifications = 50,
    onNotification,
  } = options;

  const queryClient = useQueryClient();
  const [notifications, setNotifications] = useState<RequestNotification[]>([]);
  const [latestNotification, setLatestNotification] =
    useState<RequestNotification | null>(null);
  const onNotificationRef = useRef(onNotification);

  // Update ref
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  /**
   * Add a notification
   */
  const addNotification = useCallback(
    (notification: RequestNotification) => {
      setNotifications((prev) => {
        // Check for duplicate
        if (prev.some((n) => n.id === notification.id)) {
          return prev;
        }

        // Add to front, trim if needed
        const updated = [notification, ...prev];
        if (updated.length > maxNotifications) {
          return updated.slice(0, maxNotifications);
        }
        return updated;
      });

      setLatestNotification(notification);

      // Call callback
      if (onNotificationRef.current) {
        onNotificationRef.current(notification);
      }
    },
    [maxNotifications],
  );

  /**
   * Invalidate related queries for two-way sync
   * This ensures UI components automatically update when WebSocket events arrive
   */
  const invalidateQueries = useCallback(
    (terreiroId?: string, targetMediumId?: string) => {
      console.log("[WS Notifications] Invalidating queries for two-way sync", {
        terreiroId,
        targetMediumId,
      });

      // 1. Invalidate request-related queries
      queryClient.invalidateQueries({ queryKey: ["request"] });
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      queryClient.invalidateQueries({ queryKey: ["pending-requests"] });

      // 2. Invalidate terreiro-specific queries
      if (terreiroId) {
        queryClient.invalidateQueries({
          queryKey: ["terreiro", terreiroId, "requests"],
        });
        queryClient.invalidateQueries({
          queryKey: ["terreiro", terreiroId, "pending"],
        });
      }

      // 3. Invalidate medium-specific queries (CRITICAL for status updates)
      // This triggers re-fetch of medium status which updates UI components
      queryClient.invalidateQueries({ queryKey: homeQueryKeys.mediumStatus });
      queryClient.invalidateQueries({ queryKey: homeQueryKeys.mediumUserData });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.mediumStatus,
      });

      if (targetMediumId) {
        queryClient.invalidateQueries({
          queryKey: mediumStatusQueryKey(targetMediumId),
        });
        queryClient.invalidateQueries({
          queryKey: ["medium", targetMediumId],
        });
        queryClient.invalidateQueries({
          queryKey: ["medium", targetMediumId, "pending-approval"],
        });
        queryClient.invalidateQueries({
          queryKey: [...profileQueryKeys.mediumStatus, targetMediumId],
        });
      }

      // 4. Invalidate profile queries (may contain medium status)
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.profile });
    },
    [queryClient],
  );

  /**
   * Handle REQUEST_CREATED event
   * Notifies admins when a medium creates a new request
   */
  useEffect(() => {
    // Only admins should receive created notifications
    if (!isAdmin) return;

    const unsubscribe = wsService.on<RequestCreatedPayload>(
      WSMessageType.REQUEST_CREATED,
      (message) => {
        const payload = message.payload;
        if (!payload?.request) return;

        const { request, medium } = payload;
        const mediumName = getMediumNameFromPayload(payload);
        const terreiroId = medium?.terreiroId;

        if (
          currentTerreiroId &&
          terreiroId &&
          terreiroId !== currentTerreiroId
        ) {
          return;
        }

        console.log("[WS Notifications] REQUEST_CREATED received", {
          requestId: request.id,
          mediumId: medium?.id,
          terreiroId,
        });

        const notification: RequestNotification = {
          id: `req-created-${request.id}-${Date.now()}`,
          type: "created",
          requestId: request.id,
          mediumId: medium?.id || "",
          mediumName,
          requestType: request.type,
          terreiroId: terreiroId || "",
          timestamp: new Date(message.timestamp),
          read: false,
          message: generateNotificationMessage("created", {
            mediumName,
            requestType: request.type,
          }),
        };

        addNotification(notification);
        invalidateQueries(terreiroId, medium?.id);
      },
    );

    return unsubscribe;
  }, [isAdmin, currentTerreiroId, addNotification, invalidateQueries]);

  /**
   * Handle REQUEST_APPROVED event
   * Notifies the medium when their request is approved
   * This is CRITICAL for two-way sync - UI should update immediately
   */
  useEffect(() => {
    const unsubscribe = wsService.on<RequestApprovedPayload>(
      WSMessageType.REQUEST_APPROVED,
      (message) => {
        const payload = message.payload;
        if (!payload?.request) return;

        const { request, medium } = payload;
        const terreiroId = message.terreiroId;

        if (isForeignTerreiroEvent(currentTerreiroId, terreiroId)) {
          return;
        }

        console.log("[WS Notifications] REQUEST_APPROVED received", {
          requestId: request.id,
          mediumId: medium?.id,
          mediumStatus: medium?.status,
          terreiroId,
        });

        // Only show notification to the user who made the request (or admin)
        // Check if this is for the current medium
        if (!isAdmin && medium?.id !== mediumId) {
          // Still invalidate queries for two-way sync even if not showing notification
          invalidateQueries(terreiroId, medium?.id);
          return;
        }

        const notification: RequestNotification = {
          id: `req-approved-${request.id}-${Date.now()}`,
          type: "approved",
          requestId: request.id,
          mediumId: medium?.id || "",
          requestType: request.type,
          terreiroId: terreiroId || "",
          timestamp: new Date(message.timestamp),
          read: false,
          message: generateNotificationMessage("approved", {
            requestType: request.type,
          }),
        };

        addNotification(notification);

        // CRITICAL: Invalidate queries to trigger UI update
        // This makes the medium status change reflect immediately in components
        invalidateQueries(terreiroId, medium?.id);
      },
    );

    return unsubscribe;
  }, [mediumId, currentTerreiroId, isAdmin, addNotification, invalidateQueries]);

  /**
   * Handle REQUEST_REJECTED event
   * Notifies the medium when their request is rejected
   * This is CRITICAL for two-way sync - UI should update immediately
   */
  useEffect(() => {
    const unsubscribe = wsService.on<RequestRejectedPayload>(
      WSMessageType.REQUEST_REJECTED,
      (message) => {
        const payload = message.payload;
        if (!payload?.request) return;

        const { request, medium } = payload;
        const terreiroId = message.terreiroId;

        if (isForeignTerreiroEvent(currentTerreiroId, terreiroId)) {
          return;
        }

        console.log("[WS Notifications] REQUEST_REJECTED received", {
          requestId: request.id,
          mediumId: medium?.id,
          mediumStatus: medium?.status,
          reviewNotes: request.reviewNotes,
          terreiroId,
        });

        // Only show notification to the user who made the request (or admin)
        if (!isAdmin && medium?.id !== mediumId) {
          // Still invalidate queries for two-way sync
          invalidateQueries(terreiroId, medium?.id);
          return;
        }

        const notification: RequestNotification = {
          id: `req-rejected-${request.id}-${Date.now()}`,
          type: "rejected",
          requestId: request.id,
          mediumId: medium?.id || "",
          requestType: request.type,
          terreiroId: terreiroId || "",
          timestamp: new Date(message.timestamp),
          read: false,
          message: generateNotificationMessage("rejected", {
            requestType: request.type,
            reason: request.reviewNotes,
          }),
        };

        addNotification(notification);

        // CRITICAL: Invalidate queries to trigger UI update
        // This makes the medium status change (REJECTED) reflect immediately
        invalidateQueries(terreiroId, medium?.id);
      },
    );

    return unsubscribe;
  }, [mediumId, currentTerreiroId, isAdmin, addNotification, invalidateQueries]);

  /**
   * Handle REQUEST_UPDATED event
   * Triggered when a request is updated (status change, notes added, etc.)
   * Does not create a notification, but invalidates cache for two-way sync
   */
  useEffect(() => {
    const unsubscribe = wsService.on<RequestUpdatedPayload>(
      WSMessageType.REQUEST_UPDATED,
      (message) => {
        const payload = message.payload;
        if (!payload?.request) return;

        const { request, previousStatus } = payload;
        const terreiroId = message.terreiroId;

        if (isForeignTerreiroEvent(currentTerreiroId, terreiroId)) {
          return;
        }

        console.log("[WS Notifications] REQUEST_UPDATED received", {
          requestId: request.id,
          status: request.status,
          previousStatus,
          terreiroId,
        });

        // Invalidate cache for two-way sync (no notification shown for updates)
        // This ensures UI reflects the latest state
        invalidateQueries(terreiroId);
      },
    );

    return unsubscribe;
  }, [currentTerreiroId, invalidateQueries]);

  /**
   * Handle REQUEST_CANCELLED event
   * Notifies admins that a request was cancelled and keeps data in sync.
   */
  useEffect(() => {
    if (!isAdmin) return;

    const unsubscribe = wsService.on<RequestCancelledPayload>(
      WSMessageType.REQUEST_CANCELLED,
      (message) => {
        const payload = message.payload;
        if (!payload?.request) return;

        const { request } = payload;
        const terreiroId = message.terreiroId;

        if (isForeignTerreiroEvent(currentTerreiroId, terreiroId)) {
          return;
        }

        const notification: RequestNotification = {
          id: `req-cancelled-${request.id}-${Date.now()}`,
          type: "cancelled",
          requestId: request.id,
          mediumId: "",
          requestType: request.type,
          terreiroId: terreiroId || "",
          timestamp: new Date(message.timestamp),
          read: false,
          message: generateNotificationMessage("cancelled", {
            requestType: request.type,
          }),
        };

        addNotification(notification);
        invalidateQueries(terreiroId);
      },
    );

    return unsubscribe;
  }, [isAdmin, currentTerreiroId, addNotification, invalidateQueries]);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback((notificationId: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)),
    );
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  /**
   * Clear all notifications
   */
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  /**
   * Clear latest notification
   */
  const clearLatest = useCallback(() => {
    setLatestNotification(null);
  }, []);

  /**
   * Calculate unread count
   */
  const unreadCount = notifications.filter((n) => !n.read).length;

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAll,
    latestNotification,
    clearLatest,
  };
}
